import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Skeleton,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { VisibilityOff, DeleteOutline, PushPin } from '@mui/icons-material';
import { StarIcon, DotsThreeIcon, ShareFatIcon, FlagIcon, TrashIcon } from '@phosphor-icons/react';
import { User } from 'firebase/auth';
import { font } from '../../utils/fontHelper';
import { useToast } from '../utils/ToastProvider';
import { unifiedSetDoc, CommentsService, type Comment } from '@watchlist/shared';
import {
  bundleIdFromFeedItem,
  CrewFeedItem as CrewFeedItemData,
  headerSegments,
  relativeTime,
  Seg,
} from '../../utils/crewFeedUtils';
import ImageShimmer from './ImageShimmer';
import CompactMovieCard from './CompactMovieCard';
import InitialsAvatar from './InitialsAvatar';
import WatchlistWidget from './WatchlistWidget';
import CrewPostLinkSections from './CrewPostLinkSections';
import { unifiedGetDoc } from '@watchlist/shared';
import { useCrewActivityMovieDisplay } from '../../hooks/useCrewActivityMovieDisplay';
import { stashMovieDetailNavPreviewFromMovie } from '../../utils/movieDetailNavPreview';
import PostActionBar from './PostActionBar';
import ShareBottomSheet from '../dialogs/ShareBottomSheet';
import type { SocialPostData } from '../../utils/socialPostBuilders';
import { glassStyles } from '../../tokens/designTokens';
import { scrollFadeMaskSx } from '../../utils/scrollStyles';
import { useMovieQuickView } from '../../contexts/MovieQuickViewContext';
import { formatWatchedDate } from '../../utils/dateUtils';

// Module-level cache: persists watchlist Firestore data across navigation unmounts
// so LiveWatchlistWidget never shows a blank movie count on re-mount.
const _watchlistDataCache = new Map<string, any>();

interface CrewFeedItemProps {
  item: CrewFeedItemData;
  /** Pre-hydrated SocialPostData carrying live engagement counts. Built by CrewPage via socialPostBuilders. */
  post?: SocialPostData;
  actorName: string;
  actorAvatar?: string;
  currentUser?: User | null;
  onMovieSelect?: (movieId: string) => void;
  onActorSelect?: (userId: string) => void;
  onWatchlistSelect?: (watchlistId: string) => void;
  onReviewSelect?: (reviewId: string) => void;
  onPersonSelect?: (personId: string) => void;
  /** Called when user hides their own activity from crew feed */
  onHideFromCrew?: (activityIds: string[]) => void;
  /** Called when the post author deletes their own crew post */
  onDeleteCrewPost?: (postDocId: string) => void;
  /** Admin/moderator delete — deletes any post type directly from the feed */
  onAdminDelete?: (post: SocialPostData) => void;
  /** Tap ❤️ — toggles like directly (no dialog). Receives the post payload. */
  onLikeToggle?: (post: SocialPostData) => void;
  /** Tap 💬 — opens the unified SocialPostDialog. Optional second arg scrolls to that comment. */
  onOpenPostDialog?: (post: SocialPostData, highlightCommentId?: string) => void;
  /** Tap the like count — opens LikedByBottomSheet. */
  onLikedByClick?: (post: SocialPostData) => void;
  /** Optional accessory rendered in the top-right of the header (e.g. inline Follow button on the All tab). */
  headerAccessory?: React.ReactNode;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const TMDB_IMG = (path: string | undefined | null, size = 'w342'): string | undefined => {
  if (!path || path === 'N/A') return undefined;
  if (path.startsWith('http')) return path;
  // Ensure the TMDb path starts with /
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `https://image.tmdb.org/t/p/${size}${normalized}`;
};

// Number of individual cards before switching to poster-strip layout
const STRIP_THRESHOLD = 3;

// ─── Reusable sub-components ───────────────────────────────────────────────

/** Bold-name header: "**Name** verb" + timestamp on next line */
const Header: React.FC<{
  segments: Seg[];
  timeLabel: string;
  actorAvatar?: string;
  actorName: string;
  actorId: string;
  subtitle?: string;
  onUserSelect?: (userId: string) => void;
  /** Called when the text content area (name/subtitle/time) is tapped. */
  onHeaderClick?: () => void;
  /** Render a menu button on the right side (for current user's own posts) */
  menuButton?: React.ReactNode;
  /** Optional accessory rendered on the right side (e.g. inline Follow button). Takes precedence over menuButton when both are provided. */
  headerAccessory?: React.ReactNode;
}> = ({ segments, timeLabel, actorAvatar, actorName, actorId, subtitle, onUserSelect, onHeaderClick, menuButton, headerAccessory }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
      <Box
        onClick={onUserSelect ? () => onUserSelect(actorId) : undefined}
        sx={{ cursor: onUserSelect ? 'pointer' : 'default', flexShrink: 0, mt: 0.5 }}
      >
        <InitialsAvatar src={actorAvatar} name={actorName} size={34} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* On mobile: title grows to push actions right. On md+: actions sit inline after title. */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <Box
            onClick={onHeaderClick ? (e) => { e.stopPropagation(); onHeaderClick(); } : undefined}
            sx={{ flex: { xs: 1, md: 'none' }, minWidth: 0, ...(onHeaderClick && { cursor: 'pointer' }) }}
          >
            <Typography
              component="span"
              sx={{ fontFamily: font(), fontSize: 14, lineHeight: 1.4, color: theme.palette.text.primary }}
            >
              {segments.map((seg, i) => {
                if (seg.bold && seg.profileUserId && onUserSelect) {
                  return (
                    <Box
                      key={i}
                      component="span"
                      onClick={() => onUserSelect(seg.profileUserId!)}
                      sx={{ cursor: 'pointer', display: 'inline' }}
                    >
                      <strong>{seg.text}</strong>
                    </Box>
                  );
                }
                if (seg.bold) {
                  return <strong key={i}>{seg.text}</strong>;
                }
                return <span key={i}>{seg.text}</span>;
              })}
            </Typography>
          </Box>
          {(headerAccessory || menuButton) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, mt: '-2px' }}>
              {headerAccessory}
              {menuButton}
            </Box>
          )}
        </Box>
        <Typography sx={{ fontFamily: font(), fontSize: 12, color: theme.palette.text.secondary, mt: -0.25 }}>
          {timeLabel}
        </Typography>
        {subtitle ? (
          <Typography
            sx={{
              fontFamily: font(),
              fontSize: 14,
              color: theme.palette.text.secondary,
              mt: 0.5,
              lineHeight: 1.45,
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

/**
 * Read-only movie list card that matches StandardMovieCard variant="list" visually.
 * Uses the same colors, sizing, border-radius, and layout as the watchlist detail cards.
 * Rating shown as orange star + number (matching RecentlyRatedCarousel and StandardMovieCard's own rating row).
 */
const CrewMovieCard: React.FC<{
  poster?: string;
  title: string;
  year?: string;
  genre?: string;
  rating?: number;
  onClick?: () => void;
}> = ({ poster, title, year, genre, rating, onClick }) => (
  <CompactMovieCard
    poster={poster}
    title={title}
    year={year}
    genre={genre}
    rating={rating}
    onClick={onClick}
  />
);

/** Poster strip card (portrait, matching RecentlyRatedCarousel style) */
const PosterStripCard: React.FC<{
  poster?: string;
  title: string;
  rating?: number;
  onClick?: () => void;
}> = ({ poster, title, rating, onClick }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box
      onClick={onClick}
      sx={{
        flexShrink: 0,
        width: { xs: 110, sm: 130 },
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease',
        '&:hover': onClick ? { transform: 'scale(1.03)' } : {},
        // Force GPU compositing layer so the rating overlay (sibling to the
        // WebkitTransform:translateZ(0) img inside ImageShimmer) isn't dropped
        // by the iOS compositor during scroll.
        WebkitTransform: 'translateZ(0)',
        transform: 'translateZ(0)',
      }}
    >
      <Box sx={{
        width: '100%', aspectRatio: '2/3', borderRadius: 2, overflow: 'hidden',
        position: 'relative',
        bgcolor: isDark ? '#3A3A3C' : '#F0F0F0',
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.1)',
      }}>
        <ImageShimmer
          src={TMDB_IMG(poster, 'w200')}
          alt={title} width="100%" height="100%" objectFit="cover"
          borderRadius={2}
        />
        {rating != null && (
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 4,
            background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 60%, transparent 100%)',
            p: 1, pt: 2,
            WebkitTransform: 'translateZ(0)',
            transform: 'translateZ(0)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StarIcon size={14} color="#FFD700" weight="fill" />
              <Typography sx={{ fontFamily: font(), color: 'rgba(255,255,255,0.9)', fontSize: 14, fontWeight: 600 }}>
                {rating}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Typography sx={{
        fontFamily: font(), fontSize: 11, mt: 0.5,
        color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {title}
      </Typography>
    </Box>
  );
};

const CrewMovieCardHydrated: React.FC<{
  movieId?: string;
  poster?: string | null;
  title: string;
  year?: string;
  genre?: string;
  rating?: number;
  onClick?: () => void;
}> = ({ movieId, poster, title, year, genre, rating, onClick }) => {
  const d = useCrewActivityMovieDisplay(movieId, poster, title, year, genre);
  return (
    <CrewMovieCard
      poster={d.poster}
      title={d.title}
      year={d.year}
      genre={d.genre}
      rating={rating}
      onClick={onClick}
    />
  );
};

const PosterStripCardHydrated: React.FC<{
  movieId?: string;
  poster?: string | null;
  title: string;
  rating?: number;
  onClick?: () => void;
}> = ({ movieId, poster, title, rating, onClick }) => {
  const d = useCrewActivityMovieDisplay(movieId, poster, title, undefined, undefined);
  return (
    <PosterStripCard poster={d.poster} title={d.title} rating={rating} onClick={onClick} />
  );
};

// ─── Live watchlist widget ─────────────────────────────────────────────────
/**
 * Fetches live watchlist data from Firestore on mount so the movie count and
 * poster strip always reflect the current state of the list, even when movies
 * have been added since the activity was recorded.
 */
const LiveWatchlistWidget: React.FC<{
  watchlistId: string;
  fallbackName: string;
  actorId: string;
  currentUser?: User | null;
  onClick?: () => void;
}> = ({ watchlistId, fallbackName, actorId, currentUser, onClick }) => {
  const [liveData, setLiveData] = useState<any>(_watchlistDataCache.get(watchlistId) ?? null);

  useEffect(() => {
    if (_watchlistDataCache.has(watchlistId)) return;
    unifiedGetDoc('watchlists', watchlistId)
      .then(doc => {
        if (doc?.exists?.()) {
          const data = doc.data();
          _watchlistDataCache.set(watchlistId, data);
          setLiveData(data);
        }
      })
      .catch(() => {});
  }, [watchlistId]);

  // Extract up to 2 posters from live movie details for the list variant thumbnail
  const moviePosters: string[] = liveData?.movieDetails
    ? liveData.movieDetails
        .slice(0, 2)
        .map((m: any) => m.poster || m.Poster || m.poster_path)
        .filter(Boolean)
    : [];

  const externalData = liveData
    ? {
        name: liveData.name || fallbackName,
        description: liveData.description,
        isPrivate: false,
        createdBy: actorId,
        createdByName: '',
        movieCount: liveData.movieCount ?? liveData.movieDetails?.length ?? 0,
        movieDetails: liveData.movieDetails || [],
        followerCount: liveData.followerCount || 0,
        collaborators: liveData.collaborators || [],
        collaboratorProfiles: liveData.collaboratorProfiles || [],
      }
    : {
        // While loading, show the name from the activity but no movie count
        name: fallbackName,
        description: undefined,
        isPrivate: false,
        createdBy: actorId,
        createdByName: '',
      };

  return (
    <WatchlistWidget
      watchlistType={watchlistId}
      user={currentUser}
      targetUserId={actorId}
      isOtherUserWatchlist
      moviePosters={moviePosters}
      onClick={onClick}
      externalWatchlistData={externalData}
    />
  );
};

// ─── Main component ────────────────────────────────────────────────────────

const CrewFeedItem: React.FC<CrewFeedItemProps> = ({
  item,
  actorName,
  actorAvatar,
  currentUser,
  onActorSelect,
  onWatchlistSelect,
  onReviewSelect,
  onPersonSelect,
  onHideFromCrew,
  onDeleteCrewPost,
  onAdminDelete,
  post,
  onLikeToggle,
  onOpenPostDialog,
  onLikedByClick,
  headerAccessory,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dividerColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const { openMovieQuickView } = useMovieQuickView();
  const { showSuccess, showError } = useToast();

  // Menu state for hide from crew action
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const menuOpen = Boolean(menuAnchor);

  const [topComment, setTopComment] = useState<Comment | null>(null);

  useEffect(() => {
    if (!post || post.commentCount === 0) { setTopComment(null); return; }
    const parentType = post.kind === 'customPost' ? 'crewPost'
      : post.kind === 'review' ? 'review'
      : post.kind === 'watchlist' ? 'watchlist'
      : 'bundle';
    CommentsService.getTopComment({ type: parentType, id: post.id })
      .then(setTopComment)
      .catch(() => {});
  }, [post?.id, post?.commentCount]); // eslint-disable-line react-hooks/exhaustive-deps


  const segments = headerSegments(item, actorName);
  const timeLabel = relativeTime(item.createdAt);

  // Check if this is the current user's own activity (not crew_post, those are official)
  const isOwnActivity = currentUser?.uid === item.actorId && item.kind !== 'crew_post';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleHideFromCrew = () => {
    handleMenuClose();
    if (onHideFromCrew && item.activities.length > 0) {
      // Pass all activity IDs in the bundle to hide
      const activityIds = item.activities.map(a => a.id);
      onHideFromCrew(activityIds);
    }
  };

  // Crew-post delete menu shown when the caller (CrewPage) decides the user can delete this post
  const isOwnCrewPost = item.kind === 'crew_post' && !!onDeleteCrewPost;

  // Report menu shown for other users' posts when the viewer is signed in
  const isOtherUserPost = !!currentUser && currentUser.uid !== item.actorId;

  const handleReportPost = async () => {
    handleMenuClose();
    if (!currentUser) return;
    const contentType = item.kind === 'crew_post' ? 'crewPost' : 'bundle';
    const contentId = item.kind === 'crew_post' ? (item.crewPost?.postDocId ?? item.actorId) : (bundleIdFromFeedItem(item) ?? item.actorId);
    try {
      const reportId = `${contentType}_${contentId}_${currentUser.uid}`;
      await unifiedSetDoc('reports', reportId, {
        contentType,
        contentId,
        reporterUid: currentUser.uid,
        contentAuthorUid: item.actorId,
        contentLabel: actorName,
        reportedAt: new Date().toISOString(),
      }, true);
      showSuccess('Report submitted. Thank you for helping keep the community safe.');
    } catch (e) {
      console.error('CrewFeedItem: report failed', e);
      showError('Could not submit report. Please try again.');
    }
  };

  const overflowIconSx = {
    color: theme.palette.text.secondary,
    p: 0.5,
    flexShrink: 0,
    alignSelf: 'flex-start' as const,
    '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
  };

  const adminDeleteItem = onAdminDelete && post ? (
    <MenuItem onClick={() => { handleMenuClose(); onAdminDelete!(post); }}>
      <ListItemIcon>
        <DeleteOutline fontSize="small" sx={{ color: theme.palette.error.main }} />
      </ListItemIcon>
      <ListItemText
        primary="Delete post (admin)"
        primaryTypographyProps={{ fontFamily: font(), fontSize: 14, color: theme.palette.error.main }}
      />
    </MenuItem>
  ) : null;

  const hasMenu = (isOwnActivity && !!onHideFromCrew) || isOwnCrewPost || isOtherUserPost || !!adminDeleteItem;

  const menuButton = hasMenu ? (
    <>
      <IconButton size="small" onClick={handleMenuOpen} sx={overflowIconSx}>
        <DotsThreeIcon size={20}  weight="bold" />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { ...glassStyles.menu(isDark), minWidth: 180 } }}
      >
        {isOwnActivity && onHideFromCrew && (
          <MenuItem onClick={handleHideFromCrew}>
            <ListItemIcon>
              <VisibilityOff fontSize="small" sx={{ color: theme.palette.text.secondary }} />
            </ListItemIcon>
            <ListItemText
              primary="Hide from Crew"
              primaryTypographyProps={{ fontFamily: font(), fontSize: 14, color: theme.palette.text.primary }}
            />
          </MenuItem>
        )}
        {isOwnCrewPost && (
          <MenuItem onClick={() => { handleMenuClose(); onDeleteCrewPost!(item.crewPost!.postDocId); }}>
            <ListItemIcon>
              <DeleteOutline fontSize="small" sx={{ color: theme.palette.error.main }} />
            </ListItemIcon>
            <ListItemText
              primary="Delete Post"
              primaryTypographyProps={{ fontFamily: font(), fontSize: 14, color: theme.palette.error.main }}
            />
          </MenuItem>
        )}
        {isOtherUserPost && (
          <MenuItem onClick={handleReportPost}>
            <ListItemIcon>
              <FlagIcon size={18} color={theme.palette.text.secondary} />
            </ListItemIcon>
            <ListItemText
              primary="Report post"
              primaryTypographyProps={{ fontFamily: font(), fontSize: 14, color: theme.palette.text.primary }}
            />
          </MenuItem>
        )}
        {adminDeleteItem}
      </Menu>
    </>
  ) : null;

  /**
   * Action bar shown on every social post (review / customPost / watchlist / bundle).
   * Heart toggles like in place; comment opens the unified SocialPostDialog;
   * count opens LikedByBottomSheet. Returns null when no post is hydrated yet.
   */
  const renderActionBar = (trailing?: React.ReactNode) => {
    if (!post || !onLikeToggle || !onOpenPostDialog) return null;
    const isLiked = Boolean(currentUser && post.likedBy.includes(currentUser.uid));
    return (
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: { xs: 'space-between', md: 'flex-start' }, gap: { md: 0.5 } }}>
        <PostActionBar
          likeCount={post.likeCount}
          isLiked={isLiked}
          commentCount={post.commentCount}
          onLikeToggle={() => onLikeToggle(post)}
          onCommentClick={() => onOpenPostDialog(post)}
          onLikedByClick={onLikedByClick ? () => onLikedByClick(post) : undefined}
          disabled={!currentUser}
        />
        {trailing}
      </Box>
    );
  };

  const renderTopComment = () => {
    if (!topComment || !post || !onOpenPostDialog) return null;
    return (
      <Box
        onClick={() => onOpenPostDialog(post, topComment.id)}
        sx={{
          mt: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          cursor: 'pointer',
          '&:hover': { opacity: 0.75 },
          minWidth: 0,
        }}
      >
        <InitialsAvatar
          src={topComment.authorAvatar ?? undefined}
          name={topComment.authorName}
          size={20}
        />
        <Typography
          component="span"
          sx={{ fontFamily: font(), fontSize: 13, fontWeight: 600, color: theme.palette.text.primary, flexShrink: 0 }}
        >
          {topComment.authorName}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontFamily: font(), fontSize: 13, color: theme.palette.text.secondary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {topComment.text}
        </Typography>
      </Box>
    );
  };

  const header = (
    <Header
      segments={segments}
      timeLabel={timeLabel}
      actorAvatar={actorAvatar}
      actorName={actorName}
      actorId={item.actorId}
      subtitle={undefined}
      onUserSelect={onActorSelect}
      menuButton={menuButton}
      headerAccessory={headerAccessory}
    />
  );

  // ── Official Watchlist Crew post (moderator console) ─────────────────────
  if (item.kind === 'crew_post' && item.crewPost) {
    const isActivePin =
      item.crewPost.isPinned &&
      item.crewPost.pinExpiresAtMs != null &&
      item.crewPost.pinExpiresAtMs > Date.now();

    const pinIcon = isActivePin ? (
      <PushPin sx={{ fontSize: 14, color: theme.palette.text.disabled, transform: 'rotate(45deg)', flexShrink: 0 }} />
    ) : null;

    const crewHeaderRight = (pinIcon || menuButton) ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, alignSelf: 'flex-start' }}>
        {pinIcon}
        {menuButton}
      </Box>
    ) : null;

    return (
      <Box
        sx={{ pb: 2, mb: 2, borderBottom: `1px solid ${dividerColor}` }}
      >
        <Header
          segments={segments}
          timeLabel={timeLabel}
          actorAvatar={actorAvatar}
          actorName={actorName}
          actorId={item.actorId}
          onUserSelect={onActorSelect}
          menuButton={crewHeaderRight}
        />
        {item.crewPost.subtitle && (
          <Typography sx={{ fontFamily: font(), fontSize: 14, color: theme.palette.text.secondary, mb: 1.5, lineHeight: 1.45 }}>
            {item.crewPost.subtitle}
          </Typography>
        )}

        <CrewPostLinkSections
          links={item.crewPost.links}
          crewActorId={item.actorId}
          currentUser={currentUser}
          onWatchlistSelect={onWatchlistSelect}
          onMovieSelect={openMovieQuickView}
          onReviewSelect={onReviewSelect}
          onOpenReviewDialog={onOpenPostDialog}
          onUserSelect={onActorSelect}
          onPersonSelect={onPersonSelect}
        />
        {renderActionBar()}
        {renderTopComment()}
      </Box>
    );
  }

  // ── Movie watched / rated / reviewed ──────────────────────────────────────
  if (item.kind === 'movie_watched' || item.kind === 'movie_added') {
    const [first] = item.activities;
    const isReview = first.type === 'movie_reviewed' || !!first.metadata?.review;
    const reviewText: string | undefined = first.metadata?.review as any;

    // ── Single movie with review ────────────────────────────────────────────
    if (isReview) {
      const revId = first.reviewId || (first.movieId ? `${item.actorId}_${first.movieId}` : undefined);

      // Tapping the movie card / review text opens the unified dialog (preserves crew-feed context).
      // Tapping ❤️ likes directly via the action bar — wired in renderActionBar().
      const openReviewDialog = post && onOpenPostDialog
        ? () => onOpenPostDialog(post)
        : first.movieId
        ? () => {
            if (first.metadata) {
              stashMovieDetailNavPreviewFromMovie(
                first.movieId!,
                {
                  Title: first.metadata.movieTitle || 'Film',
                  title: first.metadata.movieTitle || 'Film',
                  Year: first.metadata.movieYear,
                  year: first.metadata.movieYear,
                },
                () => first.metadata?.moviePoster || null
              );
            }
            if (revId && onReviewSelect) {
              onReviewSelect(revId);
            } else {
              openMovieQuickView(first.movieId!);
            }
          }
        : undefined;

      const shareBtn = revId ? (
        <IconButton
          size="small"
          onClick={() => setShareOpen(true)}
          sx={{ color: theme.palette.text.secondary, p: 0.75 }}
          aria-label="Share review"
        >
          <ShareFatIcon size={20} />
        </IconButton>
      ) : null;

      const FEED_LINE_CLAMP = 8;
      const reviewNeedsTruncation =
        (reviewText?.split('\n').length ?? 0) > FEED_LINE_CLAMP ||
        (reviewText?.length ?? 0) > 420;

      return (
        <Box sx={{ pb: 2.5, mb: 2, borderBottom: `1px solid ${dividerColor}` }}>
          {header}
          {reviewText && (
            reviewNeedsTruncation
              ? <Typography
                  onClick={openReviewDialog}
                  sx={{
                    fontFamily: font(), fontSize: 15,
                    color: theme.palette.text.primary, mb: 0.5,
                    fontWeight: 300, lineHeight: 1.5,
                    cursor: openReviewDialog ? 'pointer' : 'default',
                    whiteSpace: 'pre-wrap',
                    display: '-webkit-box',
                    WebkitLineClamp: FEED_LINE_CLAMP,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  "{reviewText}"
                </Typography>
              : (() => {
                  const parts = reviewText.trim().split(/\n{2,}/);
                  return (
                    <Box sx={{ mb: 2 }}>
                      {parts.map((para, i) => (
                        <Typography
                          key={i}
                          onClick={openReviewDialog}
                          sx={{
                            fontFamily: font(), fontSize: 15,
                            color: theme.palette.text.primary,
                            fontWeight: 300, lineHeight: 1.5,
                            cursor: openReviewDialog ? 'pointer' : 'default',
                            whiteSpace: 'pre-wrap',
                            mb: i < parts.length - 1 ? 1 : 0,
                          }}>
                          {i === 0 ? `"${para}` : para}{i === parts.length - 1 ? '"' : ''}
                        </Typography>
                      ))}
                    </Box>
                  );
                })()
          )}
          {reviewNeedsTruncation && openReviewDialog && (
            <Typography
              onClick={openReviewDialog}
              sx={{
                fontSize: 13, fontWeight: 500, mb: 1.25,
                color: theme.palette.primary.main,
                cursor: 'pointer',
                '&:hover': { opacity: 0.75 },
              }}>
              Read more
            </Typography>
          )}
          {first.movieId && (
            <Box sx={{ maxWidth: { xs: '100%', sm: '70%', md: '45%' } }}>
              <CrewMovieCardHydrated
                movieId={first.movieId}
                poster={first.metadata?.moviePoster}
                title={first.metadata?.movieTitle || 'Film'}
                year={first.metadata?.movieYear}
                genre={first.metadata?.movieGenre}
                rating={first.rating}
                onClick={first.movieId ? () => {
                  if (first.metadata) {
                    stashMovieDetailNavPreviewFromMovie(
                      first.movieId!,
                      {
                        Title: first.metadata.movieTitle || 'Film',
                        title: first.metadata.movieTitle || 'Film',
                        Year: first.metadata.movieYear,
                        year: first.metadata.movieYear,
                      },
                      () => first.metadata?.moviePoster || null
                    );
                  }
                  openMovieQuickView(first.movieId!);
                } : undefined}
              />
              {(first.metadata as any)?.watchedDate && (
                <Typography sx={{ fontSize: 11, color: theme.palette.text.disabled, fontFamily: font(), mt: 0.5 }}>
                  {`Watched ${formatWatchedDate((first.metadata as any).watchedDate, (first.metadata as any).watchedDatePrecision, '')}`}
                </Typography>
              )}
            </Box>
          )}
          {renderActionBar(shareBtn)}
          {renderTopComment()}
          {shareOpen && revId && (
            <ShareBottomSheet
              open={shareOpen}
              onClose={() => setShareOpen(false)}
              shareType="review"
              itemTitle={first.metadata?.movieTitle || 'Film'}
              itemId={revId}
              reviewId={revId}
              reviewMovieId={first.movieId || ''}
              reviewMovieName={first.metadata?.movieTitle || 'Film'}
              reviewMovieYear={first.metadata?.movieYear}
              reviewRating={first.rating}
              reviewText={first.metadata?.review as string | undefined}
              reviewAuthorName={actorName}
              reviewAuthorId={item.actorId}
              currentUser={currentUser ?? undefined}
              moviePoster={TMDB_IMG(first.metadata?.moviePoster, 'w780')}
            />
          )}
        </Box>
      );
    }

    // ── 1–STRIP_THRESHOLD movies → list cards, vertical on mobile / side-by-side on desktop ──
    if (!item.isBundled) {
      return (
        <Box sx={{ pb: 2.5, mb: 2, borderBottom: `1px solid ${dividerColor}` }}>
          {header}
          <Box sx={{ maxWidth: { xs: '100%', sm: item.activities.length === 1 ? '70%' : '100%', md: item.activities.length === 1 ? '45%' : '90%' } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 1,
                flexWrap: 'nowrap',
              }}
            >
              {item.activities.slice(0, STRIP_THRESHOLD).map((a, i) => (
                <Box key={a.id || i} sx={{ flex: isMobile ? 'none' : '1 1 0', minWidth: 0 }}>
                  <CrewMovieCardHydrated
                    movieId={a.movieId}
                    poster={a.metadata?.moviePoster}
                    title={a.metadata?.movieTitle || 'Film'}
                    year={a.metadata?.movieYear}
                    genre={a.metadata?.movieGenre}
                    rating={a.rating}
                    onClick={a.movieId ? () => {
                      // Stash preview data before navigation
                      if (a.metadata) {
                        stashMovieDetailNavPreviewFromMovie(
                          a.movieId!,
                          {
                            Title: a.metadata.movieTitle || 'Film',
                            title: a.metadata.movieTitle || 'Film',
                            Year: a.metadata.movieYear,
                            year: a.metadata.movieYear,
                          },
                          () => a.metadata?.moviePoster || null
                        );
                      }
                      openMovieQuickView(a.movieId!);
                    } : undefined}
                  />
                  {item.activities.length === 1 && (a.metadata as any)?.watchedDate && (
                    <Typography sx={{ fontSize: 11, color: theme.palette.text.disabled, fontFamily: font(), mt: 0.5 }}>
                      {`Watched ${formatWatchedDate((a.metadata as any).watchedDate, (a.metadata as any).watchedDatePrecision, '')}`}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
          {renderActionBar()}
          {renderTopComment()}
        </Box>
      );
    }

    // ── >STRIP_THRESHOLD movies → poster strip ──────────────────────────────
    return (
      <Box sx={{ pb: 2.5, mb: 2, borderBottom: `1px solid ${dividerColor}` }}>
        {header}
        <Box
          sx={{
            maxWidth: { xs: '100%', sm: '100%', md: '90%' },
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 0.5,
            ...scrollFadeMaskSx,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {item.activities.map((a, i) => (
            <PosterStripCardHydrated
              key={a.id || i}
              movieId={a.movieId}
              poster={a.metadata?.moviePoster}
              title={a.metadata?.movieTitle || 'Film'}
              rating={a.rating}
              onClick={a.movieId ? () => {
                // Stash preview data before navigation
                if (a.metadata) {
                  stashMovieDetailNavPreviewFromMovie(
                    a.movieId!,
                    {
                      Title: a.metadata.movieTitle || 'Film',
                      title: a.metadata.movieTitle || 'Film',
                      Year: a.metadata.movieYear,
                      year: a.metadata.movieYear,
                    },
                    () => a.metadata?.moviePoster || null
                  );
                }
                openMovieQuickView(a.movieId!);
              } : undefined}
            />
          ))}
        </Box>
        {renderActionBar()}
        {renderTopComment()}
      </Box>
    );
  }

  // ── New watchlist ─────────────────────────────────────────────────────────
  if (item.kind === 'watchlist_created') {
    const [first] = item.activities;
    const fa = first as any;
    // Support both old format (entityId/entityName) and new format (watchlistId/metadata.watchlistName)
    const watchlistId = first?.watchlistId || fa?.entityId || first?.listId;
    const watchlistName = first?.metadata?.watchlistName || fa?.entityName || first?.metadata?.listName || 'Watchlist';
    const description = first?.metadata?.watchlistDescription as string | undefined;

    return (
      <Box sx={{ pb: 2.5, mb: 2, borderBottom: `1px solid ${dividerColor}` }}>
        {header}
        <Box>
          {description && (
            <Typography sx={{
              fontFamily: font(), fontSize: 14, color: theme.palette.text.primary,
              mb: 1.25,
            }}>
              {description}
            </Typography>
          )}
          {watchlistId && (
            <Box sx={{ maxWidth: { xs: '100%', sm: '70%', md: '45%' } }}>
              <LiveWatchlistWidget
                watchlistId={watchlistId}
                fallbackName={watchlistName}
                actorId={item.actorId}
                currentUser={currentUser}
                onClick={() => onWatchlistSelect?.(watchlistId)}
              />
            </Box>
          )}
        </Box>
        {renderActionBar()}
        {renderTopComment()}
      </Box>
    );
  }

  return null;
};

export default CrewFeedItem;

// ─── Skeleton ──────────────────────────────────────────────────────────────
export const CrewFeedItemSkeleton: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const skBg = isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)';
  return (
    <Box
      sx={{
        pb: 2,
        mb: 2,
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5, minWidth: 0 }}>
        <Skeleton variant="circular" width={34} height={34} sx={{ bgcolor: skBg, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width="55%" height={16} sx={{ bgcolor: skBg }} />
          <Skeleton variant="text" width="25%" height={12} sx={{ mt: 0.5, bgcolor: skBg }} />
        </Box>
      </Box>
      <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
        <Skeleton variant="rounded" width="100%" height={88} sx={{ borderRadius: 2, bgcolor: skBg }} />
      </Box>
    </Box>
  );
};
